export type CredentialType = 'password' | 'sso';

export type AuthenticationModeValue = 'sso' | 'password_only' | 'password_and_second_factor';

type DecomposedAuthenticationMode = {
	readonly credentialType: CredentialType;
	readonly isSecondFactorRequired: boolean;
};

/**
 * Composes the screen's two controls into the single `authenticationMode` value the API expects,
 * and decomposes that value back into the two controls. `compose` ignores the switch's
 * position once the mode is `sso`: the credential lives with the identity provider, so the flag
 * carries no meaning there. `decompose` initializes the switch to `true` on `sso` — the true value
 * is unknowable from a single API value, and the strong default doubles as the safest starting
 * point should the mode later switch to a password. `decompose` reads its input as an exemption
 * list, never an inclusion one: only `password_only` clears the second factor, so any value this
 * front does not recognize — a stale row, a future mode — falls on the strong side by default,
 * mirroring `isExemptFromSecondFactor` on the server.
 */
function compose(credentialType: CredentialType, isSecondFactorRequired: boolean): AuthenticationModeValue {
	if (credentialType === 'sso') {
		return 'sso';
	}

	return isSecondFactorRequired ? 'password_and_second_factor' : 'password_only';
}

function decompose(authenticationMode: string): DecomposedAuthenticationMode {
	if (authenticationMode === 'sso') {
		return { credentialType: 'sso', isSecondFactorRequired: true };
	}

	return {
		credentialType: 'password',
		isSecondFactorRequired: authenticationMode !== 'password_only',
	};
}

const deriveAuthenticationMode = { compose, decompose };

export default deriveAuthenticationMode;
